import { z } from "zod";
import { useUrlSearchState } from "../presentation/useUrlSearchState";

const EventFiltersSchema = z.object({
	q: z.string().optional(),
	eventType: z.string().optional(),
	respaCode: z.string().optional(),
	dateFrom: z.string().optional(),
	dateTo: z.string().optional(),
	page: z.coerce.number().int().min(1).optional().default(1),
});

export type EventFilters = z.infer<typeof EventFiltersSchema>;

export interface UseEventFiltersReturn {
	filters: EventFilters;
	setFilter: <K extends keyof EventFilters>(
		key: K,
		value: EventFilters[K],
	) => void;
	removeFilter: (key: keyof EventFilters) => void;
	clearAll: () => void;
}

/**
 * URL-persisted filter state for the event search page.
 * Uses useUrlSearchState so filters survive page refresh and are shareable via URL.
 */
export function useEventFilters(): UseEventFiltersReturn {
	const [filters, setPartial] = useUrlSearchState(EventFiltersSchema);

	return {
		filters,
		setFilter: (key, value) => setPartial({ [key]: value }),
		removeFilter: (key) => {
			const next = { ...filters } as Partial<EventFilters>;
			delete next[key];
			setPartial(next as EventFilters);
		},
		clearAll: () =>
			setPartial({
				q: undefined,
				eventType: undefined,
				respaCode: undefined,
				dateFrom: undefined,
				dateTo: undefined,
				page: 1,
			}),
	};
}
